import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle, RefreshCw, Archive, Image as ImageIcon, ShieldAlert, AlertTriangle } from "lucide-react";

const CLASSIFICATIONS = [
  { key: "illustrative_image",   label: "Illustrative Image" },
  { key: "verified_listing_image", label: "Verified Listing Image" },
  { key: "inspection_evidence",  label: "Inspection Evidence" },
  { key: "marketing_image",      label: "Marketing Image" },
  { key: "placeholder",          label: "Placeholder" },
];

const STATUS_TONE = {
  pending_review: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  approved:       "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  rejected:       "bg-red-500/10 text-red-700 border-red-500/20",
  archived:       "bg-slate-500/10 text-slate-600 border-slate-500/20",
  superseded:     "bg-slate-500/10 text-slate-600 border-slate-500/20",
  draft:          "bg-slate-500/10 text-slate-600 border-slate-500/20",
};

export default function IllustrativeImages() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "admin", "country_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [file, setFile] = useState(null);
  const [decision, setDecision] = useState({ reason: "", override_warning: false });

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/illustrative-images?limit=200${status ? "&status=" + status : ""}`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openDetail = async (iid) => {
    try {
      const r = await api.get(`/illustrative-images/${iid}`);
      setSelected(r.data);
      setDecision({ reason: "", override_warning: false });
    } catch (e) { toast.error("Failed"); }
  };

  const doUpload = async () => {
    if (!file) return toast.error("Pick a file first");
    if (!form.make || !form.model || !form.asset_category) return toast.error("Category, make and model required");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => v != null && v !== "" && fd.append(k, v));
    try {
      setBusy(true);
      const r = await api.post("/illustrative-images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · confidence ${Math.round((r.data.analysis?.confidence || 0) * 100)}%`);
      setUploadOpen(false); setFile(null); setForm(defaultForm());
      await load(); openDetail(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const act = async (action) => {
    if (!selected) return;
    if (action === "reject" && !decision.reason.trim()) return toast.error("Rejection reason required");
    try {
      await api.post(`/illustrative-images/${selected.id}/${action}`, decision);
      toast.success(`${selected.id} · ${action}`);
      await load(); await openDetail(selected.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "approval_blocked") {
        toast.error("Approval blocked — AI flagged mismatch. Set 'Override warning' + reason to force.");
      } else toast.error(d?.reason || d || `${action} failed`);
    }
  };

  const conf = selected?.analysis?.confidence ?? null;
  const confTone = conf == null ? "text-muted-foreground"
                   : conf >= 0.85 ? "text-emerald-600"
                   : conf >= 0.7  ? "text-amber-700" : "text-red-700";

  return (
    <div>
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="kicker">Executive · Content Governance</div>
            <h1 className="display text-4xl">Illustrative Image Curator</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Upload dealership-grade reference photographs for vehicles, property, machinery, mining equipment and every future asset class.
              Every image goes through AI validation → human approval → publication. Nothing goes live automatically — a Super Administrator
              must approve. Where confidence is low or the AI detects a mismatch, approval is blocked until an explicit override is recorded.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <select className="h-9 border border-border bg-background text-[12.5px] px-2 rounded-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ill-filter-status">
              <option value="">All statuses</option>
              <option value="pending_review">Pending review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
            </select>
            <Button variant="outline" size="sm" onClick={load} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            {canWrite && (
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild><Button size="sm" data-testid="ill-upload-open"><Upload className="h-3.5 w-3.5 mr-1" /> Upload image</Button></DialogTrigger>
                <DialogContent className="max-w-lg" data-testid="ill-upload-dialog">
                  <DialogHeader>
                    <DialogTitle>Upload illustrative image</DialogTitle>
                    <DialogDescription>Image will be scanned, analysed by AI vision, and queued for human approval. Approval is blocked automatically if a mismatch is detected.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <F label="File" span={2}><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ill-file" className="text-[12.5px]" /></F>
                    <F label="Asset category"><select className="h-8 w-full border border-border bg-background text-[12.5px] px-2 rounded-sm" value={form.asset_category} onChange={(e) => setForm({ ...form, asset_category: e.target.value })} data-testid="ill-category"><option value="vehicle">Vehicle</option><option value="property">Property</option><option value="machinery">Machinery</option><option value="mining">Mining equipment</option><option value="commodity">Commodity</option><option value="infrastructure">Infrastructure</option></select></F>
                    <F label="Classification"><select className="h-8 w-full border border-border bg-background text-[12.5px] px-2 rounded-sm" value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })}>{CLASSIFICATIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</select></F>
                    <F label="Make"><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="ill-make" className="h-8 text-[12.5px]" /></F>
                    <F label="Model"><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="ill-model" className="h-8 text-[12.5px]" /></F>
                    <F label="Generation"><Input value={form.generation} onChange={(e) => setForm({ ...form, generation: e.target.value })} className="h-8 text-[12.5px]" /></F>
                    <F label="Variant"><Input value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} className="h-8 text-[12.5px]" /></F>
                    <F label="Year from"><Input type="number" value={form.year_from} onChange={(e) => setForm({ ...form, year_from: e.target.value })} className="h-8 text-[12.5px]" /></F>
                    <F label="Year to"><Input type="number" value={form.year_to} onChange={(e) => setForm({ ...form, year_to: e.target.value })} className="h-8 text-[12.5px]" /></F>
                    <F label="Body type"><Input value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })} placeholder="suv / pickup / sedan / ..." className="h-8 text-[12.5px]" /></F>
                    <F label="Country ISOs (comma)"><Input value={form.country_applicability} onChange={(e) => setForm({ ...form, country_applicability: e.target.value.toUpperCase() })} placeholder="ZW,KE,NG" className="h-8 text-[12.5px]" /></F>
                    <F label="Copyright / source" span={2}><Input value={form.copyright_source} onChange={(e) => setForm({ ...form, copyright_source: e.target.value })} placeholder="e.g. dealership · Wikimedia CC BY-SA · own photograph" className="h-8 text-[12.5px]" /></F>
                    <F label="Notes" span={2}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-[12.5px]" /></F>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                    <Button onClick={doUpload} disabled={busy || !file} data-testid="ill-upload-submit"><Upload className="h-3.5 w-3.5 mr-1" /> Upload &amp; analyse</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </header>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-4">
          {/* Grid */}
          <div className="border border-border bg-card">
            <div className="p-3 border-b border-border kicker">{items.length} image{items.length !== 1 ? "s" : ""}</div>
            <div className="max-h-[75vh] overflow-y-auto divide-y divide-border">
              {items.map((r) => {
                const thumbUrl = r.public_url ? `${process.env.REACT_APP_BACKEND_URL}${r.thumbnail_url}` : "/catalog/pending-verification.svg";
                const conf = r.analysis?.confidence ?? null;
                return (
                  <button key={r.id} onClick={() => openDetail(r.id)} className={`w-full text-left px-3 py-3 hover:bg-muted/40 flex gap-3 ${selected?.id === r.id ? "bg-primary/5" : ""}`} data-testid={`ill-row-${r.id}`}>
                    <img src={thumbUrl} className="h-16 w-24 object-cover bg-muted rounded-sm" alt="" onError={(e) => { e.currentTarget.src = "/catalog/pending-verification.svg"; }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.make} {r.model} {r.generation ? `· ${r.generation}` : ""}</div>
                      <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground truncate">
                        {r.asset_category} · {r.classification} {r.country_applicability?.length ? "· " + r.country_applicability.join("/") : ""}
                      </div>
                      <div className="mt-1 flex gap-1.5 items-center">
                        <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${STATUS_TONE[r.status] || ""}`}>{r.status}</span>
                        {conf != null && <span className="mono text-[9px] text-muted-foreground">AI {Math.round(conf * 100)}%</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
              {items.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No records for this filter.</div>}
            </div>
          </div>

          {/* Detail */}
          <div className="border border-border bg-card p-5 max-h-[85vh] overflow-y-auto">
            {!selected && <div className="text-sm text-muted-foreground">Select an image or upload a new one.</div>}
            {selected && (
              <div data-testid="ill-detail">
                <div className="flex items-start gap-4 flex-wrap">
                  <img src={selected.public_url ? `${process.env.REACT_APP_BACKEND_URL}${selected.public_url}` : "/catalog/pending-verification.svg"}
                       alt="" className="w-72 h-48 object-cover bg-muted rounded-sm" onError={(e) => { e.currentTarget.src = "/catalog/pending-verification.svg"; }} />
                  <div className="flex-1 min-w-0">
                    <div className="mono text-[10.5px] uppercase tracking-widest text-primary">{selected.id}</div>
                    <h3 className="text-lg font-semibold mt-0.5">{selected.make} {selected.model}</h3>
                    <div className="text-[12px] text-muted-foreground">{selected.asset_category} · {selected.classification} · {selected.generation || "—"}</div>
                    <div className="mt-2"><span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[selected.status] || ""}`}>{selected.status}</span></div>
                    {selected.override_warning && <div className="mt-2 mono text-[10px] uppercase tracking-widest text-amber-700"><ShieldAlert className="inline h-3 w-3 mr-1" /> Approved with override</div>}
                  </div>
                </div>

                {/* Analysis */}
                <div className="mt-5 border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="kicker">Automated Analysis</div>
                    <div className={`font-semibold ${confTone}`}>{conf != null ? `${Math.round(conf * 100)}%` : "—"}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-[12px]">
                    <D l="Detected make" v={selected.analysis?.detected?.make} same={selected.analysis?.match?.make} />
                    <D l="Detected model" v={selected.analysis?.detected?.model} same={selected.analysis?.match?.model} />
                    <D l="Detected body" v={selected.analysis?.detected?.body_style} same={selected.analysis?.match?.body_style} />
                    <D l="Orientation" v={selected.analysis?.orientation} />
                    <D l="Resolution" v={selected.analysis?.resolution ? `${selected.analysis.resolution.width}×${selected.analysis.resolution.height}` : "—"} />
                    <D l="AI provider" v={selected.analysis?.provider || "—"} />
                  </div>
                  {(selected.analysis?.warnings || []).length > 0 && (
                    <div className="mt-3 border-l-2 border-amber-500/60 pl-3">
                      {selected.analysis.warnings.map((w, i) => (
                        <div key={i} className="text-[11.5px] flex gap-1.5 items-start"><AlertTriangle className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" /> {w}</div>
                      ))}
                    </div>
                  )}
                  {selected.duplicates?.length > 0 && (
                    <div className="mt-3 mono text-[10.5px] uppercase tracking-widest text-amber-700">Duplicate detected · {selected.duplicates[0].id} ({selected.duplicates[0].status})</div>
                  )}
                </div>

                {/* Usage locations */}
                {selected.usage_locations?.length > 0 && (
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="kicker">Usage locations</div>
                    <div className="mt-1 text-[12px]">
                      {selected.usage_locations.map((u, i) => (
                        <div key={i}>• <span className="mono uppercase text-[10px]">{u.collection}</span> · {u.count} listing{u.count !== 1 ? "s" : ""}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {canWrite && ["pending_review", "rejected"].includes(selected.status) && (
                  <div className="mt-5 border-t border-border pt-4">
                    <Textarea rows={2} placeholder="Approval / rejection note (required for reject; recommended for approve)" value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })} data-testid="ill-decision-reason" />
                    <label className="flex items-start gap-2 text-[11.5px] text-muted-foreground mt-2">
                      <input type="checkbox" checked={decision.override_warning} onChange={(e) => setDecision({ ...decision, override_warning: e.target.checked })} data-testid="ill-override" />
                      Override AI warning (required when confidence &lt; 70% or a mismatch is detected).
                    </label>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Button size="sm" onClick={() => act("approve")} data-testid="ill-approve"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve &amp; publish</Button>
                      <Button size="sm" variant="outline" onClick={() => act("reject")} data-testid="ill-reject"><XCircle className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                    </div>
                  </div>
                )}
                {canWrite && selected.status === "approved" && (
                  <div className="mt-5 border-t border-border pt-4 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => act("archive")} data-testid="ill-archive"><Archive className="h-3.5 w-3.5 mr-1" /> Archive</Button>
                  </div>
                )}

                {/* History */}
                <div className="mt-5 border-t border-border pt-4">
                  <div className="kicker">History</div>
                  <ol className="mt-1 space-y-1 text-[11.5px]">
                    {(selected.history || []).slice().reverse().map((h, i) => (
                      <li key={i} className="border-l-2 border-primary/40 pl-2">
                        <div className="mono uppercase text-[10px] text-primary">{h.action}</div>
                        <div className="text-muted-foreground text-[10px]">{h.at} · {h.by}</div>
                        {h.meta?.reason && <div>{h.meta.reason}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const defaultForm = () => ({
  asset_category: "vehicle", make: "", model: "", generation: "", variant: "",
  year_from: "", year_to: "", body_type: "", classification: "illustrative_image",
  country_applicability: "", copyright_source: "", notes: "",
});

const F = ({ label, span, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    {children}
  </div>
);

const D = ({ l, v, same }) => (
  <div className="flex flex-col">
    <span className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{l}</span>
    <span className={same === false ? "text-red-700" : same === true ? "text-emerald-700" : ""}>{v || "—"}</span>
  </div>
);
